import { notification } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { GeneralMomentClient, OperatorGamePopulated } from "../shared/interfaces";
import { AntdBasicButton, COLORS } from "../styled/basic";
import { NavigationServiceInstance } from "./navigation";

/** Socket “new / updated moment” toasts: top-right, auto-dismiss, closable (antd default). */
const MOMENT_SOCKET_NOTIFICATION_DURATION_SEC = 15;

type UpdatedMomentNotificationCallback = (gMomentId: string, momentId: string) => void;

notification.config({
  placement: "topRight",
});

class NotificationService {
  public updatedMomentNotificationKeys: string[] = []

  public closeUpdatedMomentNotification = () => {
    this.updatedMomentNotificationKeys.forEach((key) => {
      notification.close(key);
    });
    this.updatedMomentNotificationKeys = [];
  }

  public showNewDraftMomentCaptured = (options: {
    game: OperatorGamePopulated;
    generalMoment: GeneralMomentClient;
    onView: () => void;
  }): void => {
    const { game, generalMoment, onView } = options;
    const notificationKey = `draft-gm-${generalMoment._id}-${Date.now()}`;

    const handleOpen = (): void => {
      notification.close(notificationKey);
      onView();
    };

    const label = (
      <div
        role="presentation"
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {game.team1?.logo ? (
            <img
              src={game.team1.logo}
              alt=""
              style={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: 4,
                background: "#f5f5f5",
              }}
            />
          ) : (
            <div style={{ width: 40, height: 40, background: "#f0f0f0", borderRadius: 4 }} />
          )}
          {game.team2?.logo ? (
            <img
              src={game.team2.logo}
              alt=""
              style={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: 4,
                background: "#f5f5f5",
              }}
            />
          ) : (
            <div style={{ width: 40, height: 40, background: "#f0f0f0", borderRadius: 4 }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {game.team1?.name ?? "Team 1"}
            {" vs "}
            {game.team2?.name ?? "Team 2"}
          </div>
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.65)", marginTop: 4 }}>
            New moment captured — click to view and label
          </div>
        </div>
      </div>
    );

    notification.open({
      message: label,
      btn: (
        <AntdBasicButton
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
          }}
        >
          View moment
        </AntdBasicButton>
      ),
      key: notificationKey,
      duration: MOMENT_SOCKET_NOTIFICATION_DURATION_SEC,
      placement: "topRight",
      onClick: handleOpen,
    });
  };

  /**
   * Is used when General Moment is updated (moment camera a different camera appeared)
   * User can go to the moment if they are on the target game's moments
   */
  public showUpdatedMomentNotification = (
    gMomentId: string,
    momentId: string,
    cameraName: string,
    callback: UpdatedMomentNotificationCallback,
  ) => {
    const notificationKey = `${Date.now()}-${momentId}`;
    const button = (
      <AntdBasicButton
        onClick={() => {
          notification.close(notificationKey);
          callback(gMomentId, momentId);
        }}
      >
        View
      </AntdBasicButton>
    );

    this.updatedMomentNotificationKeys.push(notificationKey);

    notification.open({
      btn: button,
      message: `One of the moments was updated. New images from "${cameraName}" camera`,
      key: notificationKey,
      duration: MOMENT_SOCKET_NOTIFICATION_DURATION_SEC,
      placement: "topRight",
    });
  }

  public showErrorNotification = (message: string, duration?: number): void => {
    notification.open({
      message: "Error",
      description: message,
      duration: duration || 4.5,
      icon: <InfoCircleOutlined style={{ color: COLORS.RED }} />,
    });
  };

  public showUnexpectedErrorNotification = (
    source: string, message: string, fullError: unknown = null,
  ): void => {
    if (fullError) {
      console.error(`>> ${source} full error: `, fullError);
    }

    const errorContent = `Error occured while calling '${source}'. Message: ${message}.`;

    console.error(`>> ${errorContent}`);

    notification.open({
      message: "Error",
      description: message,
      duration: 4.5,
      icon: <InfoCircleOutlined style={{ color: COLORS.RED }} />,
    });
  };

  public showInfoNotification = (message: string, duration?: number): void => {
    notification.open({
      message,
      duration: duration || 3.5,
      icon: <InfoCircleOutlined style={{ color: COLORS.BLUE_MAIN }} />,
    });
  };

  /** Live feed / polling: same placement and icon family as info errors, optional View. */
  public showOperatorFeedItemNotification = (options: {
    title: string;
    description?: string;
    href?: string;
    onClick?: () => void;
    actionLabel?: string;
  }): void => {
    const key = `op-feed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const go = (): void => {
      notification.close(key);
      if (options.onClick) {
        options.onClick();
      } else if (options.href) {
        NavigationServiceInstance.push(options.href);
      }
    };

    const btn = options.href || options.onClick ? (
      <AntdBasicButton
        type="default"
        onClick={(e) => {
          e.stopPropagation();
          go();
        }}
      >
        {options.actionLabel || "View"}
      </AntdBasicButton>
    ) : undefined;

    notification.open({
      message: options.title,
      description: options.description,
      duration: 10,
      placement: "topRight",
      key,
      icon: <InfoCircleOutlined style={{ color: COLORS.BLUE_MAIN }} />,
      btn,
    });
  };

  public showSuccessNotification = (message: string, duration?: number): void => {
    notification.success({
      message,
      duration: duration || 3.5,
    });
  };
}

export const NotificationServiceInstance = new NotificationService();
